import { useQuery } from '@tanstack/react-query';

import React, { useEffect, useState } from 'react';

import { useApiClient, useToastService } from '@@common';

import { AddComponentDependencyModel, EnvironmentClient, SearchComponentRequest, SetComponentDependenciesRequest } from '../../../api/api';
import { OperationInputDialog } from '../../FlowDesigner/Components/Inputs/OperationInputDialog';
import { OperationRefModel } from '../../FlowDesigner/Models/DomainServiceModel';

interface AddOperationDependencyDialogProps {
	open: boolean;
	componentId: string;
	componentName: string;
	onClose: () => void;
	onDependencyAdded: () => void;
}

export default function AddOperationDependencyDialog({ open, componentId, componentName, onClose, onDependencyAdded }: AddOperationDependencyDialogProps) {
	const client = useApiClient(EnvironmentClient) as EnvironmentClient;
	const toastService = useToastService();
	const [selectedOperation, setSelectedOperation] = useState<OperationRefModel | undefined>(undefined);

	// Query to fetch all components
	const { data: allComponents } = useQuery({
		queryKey: ['components-for-dependency'],
		queryFn: async () => {
			const query: SearchComponentRequest = {
				paging: {
					pageIndex: 0,
					pageSize: 1000,
					sortBy: 'Name',
					sortAscending: true,
				},
			};
			const result = await client.searchComponent(query);
			return result.items;
		},
		enabled: open,
	});

	const handleOperationChange = async (operationRef: OperationRefModel | undefined) => {
		if (!operationRef) {
			setSelectedOperation(undefined);
			onClose();
			return;
		}

		setSelectedOperation(operationRef);

		// Find the component with matching ContextId
		if (!allComponents) return;

		const matchingComponent = allComponents.find((c) => c.contextId === operationRef.ContextId && c.id !== componentId);

		if (!matchingComponent) {
			toastService.error(`No component found with ContextId: ${operationRef.ContextName || operationRef.ContextId}`);
			console.error(`No component found with ContextId: ${operationRef.ContextName || operationRef.ContextId}`, allComponents, operationRef);
			setSelectedOperation(undefined);
			return;
		}

		// Automatically add the dependency
		try {
			const dependency: AddComponentDependencyModel = {
				dependsOnComponentId: matchingComponent.id,
				targetId: operationRef.Id,
				targetType: 'Operation',
			};

			const request: SetComponentDependenciesRequest = {
				add: [dependency],
			};

			await client.setDependencies(componentId, request);
			toastService.success(`Operation dependency added: ${operationRef.Name} from component ${matchingComponent.name}`);
			setSelectedOperation(undefined);
			onDependencyAdded();
		} catch (err) {
			console.error('Error adding operation dependency:', err);
			const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
			toastService.error(`Failed to add dependency: ${errorMessage}`);
		}
	};

	// Reset state when dialog closes
	useEffect(() => {
		if (!open) {
			setSelectedOperation(undefined);
		}
	}, [open]);

	return (
		<OperationInputDialog
			open={open}
			setOpen={(isOpen) => {
				if (!isOpen) {
					onClose();
				}
			}}
			operationRef={selectedOperation}
			onOperationRefChange={handleOperationChange}
		/>
	);
}
